import type { ReactNode } from 'react';

import type { OperationDetail, TransitionOption } from '@/shared/api/resources';
import { formatCpfCnpj } from '@/shared/lib/format';
import { Badge, type BadgeTone } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody } from '@/shared/ui/Card';
import { Icon } from '@/shared/ui/Icon';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';

import { OperationAuthorityPanel } from '../creditAuthority/OperationAuthorityPanel';
import { GenerationPanel } from '../generation/GenerationPanel';
import { AnalysisPanels } from './AnalysisPanels';
import { GuarantorsPanel } from './GuarantorsPanel';
import { IssuancePanel } from './IssuancePanel';
import { QuoteSummaryTab } from './QuoteSummaryTab';
import {
  blocosDaTela,
  etapaDaEsteira,
  quadroDoParecer,
  quadrosDaTela,
  telasVizinhas,
  type QuadroDaOperacao,
  type SituacaoDaTela,
} from './telasDaOperacao';

/** Leva ao quadro do parecer: abre a tela dele, se preciso, e rola ate ele. */
export type IrParaQuadro = (quadro: QuadroDaOperacao, ancora: string) => void;

/**
 * Movimento que ainda nao passa porque o parecer da etapa nao esta aprovado.
 *
 * Antes o botao aparecia ativo e so o clique dizia que faltava o parecer (03/11/2026):
 * quem aprovara o credito do tomador achava que o botao nao funcionava. O motivo vem do
 * servidor; o botao fica travado e o aviso leva ao quadro do parecer, nesta pagina.
 * Serve ao Proximo passo e ao Continuar da tela da etapa, com o mesmo texto.
 */
export function MovimentoTravado({
  transicao,
  onIrPara,
  rotulo,
  alinhamento = 'inicio',
}: {
  transicao: TransitionOption;
  onIrPara: IrParaQuadro;
  /** O texto do botao. Sem ele, o nome do movimento. */
  rotulo?: ReactNode;
  alinhamento?: 'inicio' | 'fim';
}) {
  const quadro = transicao.requires ? quadroDoParecer[transicao.requires] : undefined;
  const fim = alinhamento === 'fim';
  return (
    <div className={fim ? 'flex flex-col items-end gap-1.5' : 'flex flex-col gap-1.5'}>
      <Button disabled>{rotulo ?? transicao.label}</Button>
      <p className={fim ? 'text-right text-2xs text-ink-500' : 'text-2xs text-ink-500'}>
        {transicao.blocked_reason}
        {quadro
          ? ` Aprove o quadro ${quadro.nome}, nesta pagina, e depois avance a etapa.`
          : ''}
      </p>
      {quadro ? (
        <Button
          size="sm"
          variant="ghost"
          className={fim ? 'self-end' : 'self-start'}
          onClick={() => onIrPara(quadro.quadro, quadro.ancora)}
        >
          Ir para {quadro.nome}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * O cartao de identificacao, a vista em toda etapa: numero, partes, produto,
 * modalidade e situacao. O detalhe de cada parte continua no Resumo da cotacao, na
 * tela Em cotacao.
 */
export function IdentificacaoDaOperacao({ operacao }: { operacao: OperationDetail }) {
  const documentoDoTomador = operacao.policyholder.document
    ? formatCpfCnpj(operacao.policyholder.document)
    : operacao.policyholder.sublabel;
  return (
    <Card>
      <CardBody className="grid gap-x-4 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
        <Dado
          rotulo="Numero da operacao"
          valor={String(operacao.number)}
          apoio={
            operacao.proposal_number ? `Cotacao ${operacao.proposal_number}` : undefined
          }
        />
        <Dado
          rotulo="Tomador"
          valor={operacao.policyholder.label}
          apoio={documentoDoTomador || undefined}
        />
        <Dado
          rotulo="Segurado"
          valor={operacao.insured?.label ?? 'Sem segurado'}
          apoio={
            operacao.insured?.document
              ? formatCpfCnpj(operacao.insured.document)
              : undefined
          }
        />
        <Dado rotulo="Corretora" valor={operacao.broker?.label ?? 'Operacao direta'} />
        <Dado rotulo="Produto" valor={operacao.product.label} />
        <Dado rotulo="Modalidade" valor={operacao.modality.label} />
        <div className="min-w-0 sm:col-span-2">
          <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
            Situacao
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <StatusBadge kind="operacao" value={operacao.stage} />
            {operacao.proposal_status ? (
              <StatusBadge kind="cotacao" value={operacao.proposal_status} />
            ) : null}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function Dado({
  rotulo,
  valor,
  apoio,
}: {
  rotulo: string;
  valor: string;
  apoio?: string | undefined;
}) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {rotulo}
      </p>
      <p className="mt-0.5 truncate text-sm font-medium text-ink-900" title={valor}>
        {valor}
      </p>
      {apoio ? <p className="truncate text-2xs text-ink-500">{apoio}</p> : null}
    </div>
  );
}

const etiquetaDaSituacao: Record<
  Exclude<SituacaoDaTela, 'travada'>,
  { texto: string; tom: BadgeTone }
> = {
  atual: { texto: 'Etapa atual', tom: 'brand' },
  concluida: { texto: 'Etapa concluida', tom: 'success' },
  encerrada: { texto: 'Operacao encerrada', tom: 'neutral' },
};

export interface TelaDaEtapaProps {
  operacao: OperationDetail;
  /** A etapa cuja tela esta aberta. */
  etapa: string;
  situacao: Exclude<SituacaoDaTela, 'travada'>;
  /** Abre a tela de outra etapa, sem mover a operacao. */
  onAbrirEtapa: (etapa: string) => void;
  onIrPara: IrParaQuadro;
  /** O movimento da esteira, o mesmo do Proximo passo. */
  onMover: (stage: string) => void;
  movendo: boolean;
}

/**
 * A tela de uma etapa da esteira (ADR 0107): o titulo, os quadros da etapa e, no fim,
 * Voltar e Continuar.
 *
 * Voltar so navega: a esteira nao tem movimento de volta. Na etapa atual, Continuar e
 * o movimento da esteira, com o mesmo motivo de travado do Proximo passo; nas
 * concluidas e na operacao encerrada, Continuar so abre a tela seguinte.
 */
export function TelaDaEtapa({
  operacao,
  etapa,
  situacao,
  onAbrirEtapa,
  onIrPara,
  onMover,
  movendo,
}: TelaDaEtapaProps) {
  const progressao = operacao.progression;
  const posicao = progressao.indexOf(etapa);
  const nome = statusLabel('operacao', etapa);
  const etiqueta = etiquetaDaSituacao[situacao];
  const blocos = blocosDaTela(quadrosDaTela(etapa, progressao));
  const parada = etapaDaEsteira(operacao);
  const naoAconteceu =
    operacao.stage === 'declinada' && posicao > progressao.indexOf(parada);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
            Etapa {posicao + 1} de {progressao.length}
          </p>
          <h2 className="text-lg font-semibold tracking-tight text-ink-900">{nome}</h2>
          {naoAconteceu ? (
            <p className="text-xs text-ink-500">
              A operacao foi declinada na etapa {statusLabel('operacao', parada)}: esta
              etapa nao chegou a acontecer.
            </p>
          ) : null}
        </div>
        <Badge tone={etiqueta.tom} dot>
          {etiqueta.texto}
        </Badge>
      </div>

      {blocos.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-ink-500">
              Esta etapa nao tem quadro proprio. O movimento da esteira fica no Proximo
              passo.
            </p>
          </CardBody>
        </Card>
      ) : null}

      {blocos.map((bloco) =>
        bloco.tipo === 'cotacao' ? (
          <QuoteSummaryTab
            key={`cotacao-${bloco.secoes.join('-')}`}
            operacao={operacao}
            secoes={bloco.secoes}
          />
        ) : (
          <QuadroNaTela key={bloco.quadro} quadro={bloco.quadro} operacao={operacao} />
        ),
      )}

      <NavegacaoDaEtapa
        operacao={operacao}
        etapa={etapa}
        situacao={situacao}
        onAbrirEtapa={onAbrirEtapa}
        onIrPara={onIrPara}
        onMover={onMover}
        movendo={movendo}
      />
    </div>
  );
}

/** Cada quadro da pagina, como ele era montado antes da tela por etapa. */
function QuadroNaTela({
  quadro,
  operacao,
}: {
  quadro: QuadroDaOperacao;
  operacao: OperationDetail;
}) {
  switch (quadro) {
    case 'credito':
    case 'subscricao':
    case 'juridico':
      return (
        <AnalysisPanels
          operationId={operacao.id}
          stage={operacao.stage}
          pareceres={[quadro]}
          sugestao={{
            policyholderId: operacao.policyholder.id,
            grupo: operacao.modality_info?.credit_group,
            taxaBps: operacao.applied_rate_bps,
          }}
        />
      );
    case 'avalistas':
      return <GuarantorsPanel operationId={operacao.id} />;
    case 'alcadas':
      // Pareceres, alcadas e bloqueios de emissao (C-ALC).
      return <OperationAuthorityPanel operationId={operacao.id} />;
    case 'minuta':
      return <GenerationPanel operationId={operacao.id} kind="minuta" />;
    case 'ccg':
      return <GenerationPanel operationId={operacao.id} kind="ccg" />;
    case 'emissao':
      return <IssuancePanel operacao={operacao} />;
  }
}

function NavegacaoDaEtapa({
  operacao,
  etapa,
  situacao,
  onAbrirEtapa,
  onIrPara,
  onMover,
  movendo,
}: TelaDaEtapaProps) {
  const { anterior, seguinte } = telasVizinhas(etapa, operacao.progression);
  const avancos = operacao.transitions.filter((item) => item.stage !== 'declinada');

  let continuar: ReactNode = null;
  if (situacao === 'atual') {
    if (avancos.length === 0) {
      continuar = (
        <div className="flex flex-col items-end gap-1.5">
          <Button disabled>
            Continuar
            <Icon name="chevronRight" size={15} />
          </Button>
          <p className="max-w-sm text-right text-2xs text-ink-500">
            {operacao.stage === 'em_emissao'
              ? 'O proximo passo e emitir a garantia, no quadro de emissao. A apolice e a comissao nascem junto com a mudanca de etapa.'
              : 'Nenhum movimento disponivel para o seu acesso.'}
          </p>
        </div>
      );
    } else {
      continuar = (
        <div className="flex flex-col items-end gap-3">
          {avancos.map((transicao) => {
            const rotulo = (
              <>
                {avancos.length > 1 ? `Continuar: ${transicao.label}` : 'Continuar'}
                <Icon name="chevronRight" size={15} />
              </>
            );
            return transicao.blocked_reason ? (
              <div key={transicao.stage} className="flex max-w-sm flex-col items-end">
                <p className="text-right text-2xs font-medium text-ink-700">
                  {transicao.label}
                </p>
                <MovimentoTravado
                  transicao={transicao}
                  onIrPara={onIrPara}
                  rotulo={rotulo}
                  alinhamento="fim"
                />
              </div>
            ) : (
              <div
                key={transicao.stage}
                className="flex max-w-sm flex-col items-end gap-1.5"
              >
                <Button loading={movendo} onClick={() => onMover(transicao.stage)}>
                  {rotulo}
                </Button>
                <p className="text-right text-2xs text-ink-500">
                  {transicao.label}: a operacao segue para{' '}
                  {statusLabel('operacao', transicao.stage).toLowerCase()}.
                </p>
              </div>
            );
          })}
        </div>
      );
    }
  } else if (seguinte) {
    continuar = (
      <div className="flex flex-col items-end gap-1.5">
        <Button variant="soft" onClick={() => onAbrirEtapa(seguinte)}>
          Continuar
          <Icon name="chevronRight" size={15} />
        </Button>
        <p className="text-right text-2xs text-ink-500">
          Abre a tela {statusLabel('operacao', seguinte)}, sem mover a operacao.
        </p>
      </div>
    );
  }

  return (
    <nav
      aria-label="Voltar e continuar entre as etapas"
      className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-line bg-surface px-4 py-3 shadow-(--shadow-xs)"
    >
      {anterior ? (
        <div className="flex flex-col items-start gap-1.5">
          <Button variant="secondary" onClick={() => onAbrirEtapa(anterior)}>
            <Icon name="chevronLeft" size={15} />
            Voltar
          </Button>
          <p className="text-2xs text-ink-500">
            Abre a tela {statusLabel('operacao', anterior)}.
          </p>
        </div>
      ) : (
        <span />
      )}
      {continuar}
    </nav>
  );
}
